<template>
  <div class="d-flex flex-column flex-grow-1 rounded p-3">
    <BaseToolBar
      title="Indicateurs entretiens"
      subtitle="Statistiques et données sur les entretiens des véhicules"
    >
      <div class="btn-group mr-1" role="group">
        <BaseButton
          class="btn btn-primary ml-3"
          v-tooltip="'Exporter les statistiques'"
          @click="exportStats"
          :loading="loading"
          text="Exporter (.xls)"
        ></BaseButton>
      </div>
      <router-link to="/vehicules" class="btn btn-secondary">
        Retour à la liste
      </router-link>
    </BaseToolBar>

    <!-- Loading indicator -->
    <div v-if="loading" class="d-flex justify-content-center my-5">
      <div class="spinner-border text-primary" role="status">
        <span class="sr-only">Chargement...</span>
      </div>
    </div>

    <div v-else class="row mt-3">
      <!-- Cartes de statistiques générales -->
      <div class="col-md-3 mb-4">
        <div class="card h-100 border-primary">
          <div class="card-body text-center">
            <h5 class="card-title text-primary">Total des entretiens</h5>
            <div class="display-4">{{ entretiens.length }}</div>
          </div>
        </div>
      </div>
      <div class="col-md-3 mb-4">
        <div
          class="card h-100"
          :class="
            entretiensProcheExpiration.length > 0
              ? 'border-warning'
              : 'border-success'
          "
        >
          <div class="card-body text-center">
            <h5
              class="card-title"
              :class="
                entretiensProcheExpiration.length > 0
                  ? 'text-warning'
                  : 'text-success'
              "
            >
              Entretiens à prévoir
            </h5>
            <div class="display-4">{{ entretiensProcheExpiration.length }}</div>
          </div>
        </div>
      </div>
      <div class="col-md-3 mb-4">
        <div
          class="card h-100"
          :class="
            entretiensExpires.length > 0 ? 'border-danger' : 'border-success'
          "
        >
          <div class="card-body text-center">
            <h5
              class="card-title"
              :class="
                entretiensExpires.length > 0 ? 'text-danger' : 'text-success'
              "
            >
              Entretiens expirés
            </h5>
            <div class="display-4">{{ entretiensExpires.length }}</div>
          </div>
        </div>
      </div>
      <div class="col-md-3 mb-4">
        <div class="card h-100 border-info">
          <div class="card-body text-center">
            <h5 class="card-title text-info">Contrôles techniques</h5>
            <div class="display-4">{{ getEntretiensByType(2).length }}</div>
          </div>
        </div>
      </div>

      <!-- Top 10 des derniers entretiens réalisés -->
      <div class="col-md-12 mb-4">
        <div class="card h-100">
          <div class="card-header bg-secondary text-white">
            <h5 class="mb-0">
              10 derniers entretiens réalisés
            </h5>
          </div>
          <div class="card-body">
            <table class="table table-striped table-hover">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Véhicule</th>
                  <th>Immatriculation</th>
                  <th>Type</th>
                  <th>Validité</th>
                  <th class="text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="entretien in derniersEntretiens" :key="entretien.id">
                  <td>{{ formatDate(entretien.date_realisation) }}</td>
                  <td>{{ entretien.modele }}</td>
                  <td>
                    <span class="badge badge-light">{{
                      entretien.immatriculation
                    }}</span>
                  </td>
                  <td>
                    <span
                      class="badge"
                      :class="getTypeBadgeClass(entretien.type)"
                    >
                      {{ getTypeLabel(entretien.type) }}
                    </span>
                  </td>
                  <td>{{ formatDate(entretien.date_fin_validite) }}</td>
                  <td class="text-center">
                    <router-link
                      :to="
                        `/vehicules/detail/${entretien.vehicule_id}?tab=entretiens`
                      "
                      class="btn btn-sm btn-outline-primary"
                    >
                      <i class="fas fa-eye mr-1"></i> Voir
                    </router-link>
                  </td>
                </tr>
                <tr v-if="derniersEntretiens.length === 0">
                  <td colspan="6" class="text-center">
                    Aucun entretien enregistré
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Entretiens à venir et expirés -->
      <div class="col-md-12 mb-4">
        <div class="card h-100">
          <div class="card-header bg-warning text-dark">
            <h5 class="mb-0">
              Entretiens à prévoir et expirés
            </h5>
          </div>
          <div class="card-body">
            <table class="table table-striped">
              <thead>
                <tr>
                  <th>Véhicule</th>
                  <th>Immatriculation</th>
                  <th>Type</th>
                  <th>Réalisé le</th>
                  <th>Valide jusqu'au</th>
                  <th>Status</th>
                  <th class="text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="entretien in entretiensUrgents"
                  :key="entretien.id"
                  :class="
                    isEntretienExpire(entretien)
                      ? 'bg-danger-light'
                      : 'bg-warning-light'
                  "
                >
                  <td>{{ entretien.modele || "Non défini" }}</td>
                  <td>{{ entretien.immatriculation || "Non défini" }}</td>
                  <td>{{ getTypeLabel(entretien.type) }}</td>
                  <td>{{ formatDate(entretien.date_realisation) }}</td>
                  <td>{{ formatDate(entretien.date_fin_validite) }}</td>
                  <td>
                    <span class="badge" :class="getStatusBadgeClass(entretien)">
                      {{ getStatusText(entretien) }}
                    </span>
                  </td>
                  <td class="text-center">
                    <router-link
                      :to="
                        `/vehicules/detail/${entretien.vehicule_id}?tab=entretiens`
                      "
                      class="btn btn-sm btn-outline-primary"
                    >
                      <i class="fas fa-eye mr-1"></i> Voir
                    </router-link>
                  </td>
                </tr>
                <tr v-if="entretiensUrgents.length === 0">
                  <td colspan="7" class="text-center">
                    Tous les entretiens sont à jour
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Graphique d'état des entretiens -->
      <div class="col-md-6 mb-4">
        <div class="card h-100">
          <div class="card-header bg-dark text-white">
            <h5 class="mb-0">État des entretiens</h5>
          </div>
          <div class="card-body">
            <div
              class="chart-container"
              style="position: relative; height:300px;"
            >
              <!-- Remplacer la référence par le composant apexchart -->
              <apexchart
                type="donut"
                height="300"
                :options="statusChartOptions"
                :series="statusSeries"
              ></apexchart>
            </div>
          </div>
        </div>
      </div>

      <!-- Graphique répartition par type -->
      <div class="col-md-6 mb-4">
        <div class="card h-100">
          <div class="card-header bg-dark text-white">
            <h5 class="mb-0">Répartition par type</h5>
          </div>
          <div class="card-body">
            <div
              class="chart-container"
              style="position: relative; height:300px;"
            >
              <!-- Remplacer la référence par le composant apexchart -->
              <apexchart
                type="pie"
                height="300"
                :options="typeChartOptions"
                :series="typeSeries"
              ></apexchart>
            </div>
          </div>
        </div>
      </div>

      <!-- Graphique évolution mensuelle -->
      <div class="col-md-12 mb-4">
        <div class="card h-100">
          <div class="card-header bg-dark text-white">
            <h5 class="mb-0">Évolution mensuelle des entretiens</h5>
          </div>
          <div class="card-body">
            <div
              class="chart-container"
              style="position: relative; height:300px;"
            >
              <!-- Remplacer la référence par le composant apexchart -->
              <apexchart
                type="bar"
                height="300"
                :options="monthlyChartOptions"
                :series="monthlySeries"
              ></apexchart>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { errHandler } from "@/helpers.js";
import BaseToolBar from "@/components/bases/ToolBar.vue";
import BaseButton from "@/components/bases/Button.vue";
import moment from "moment";

export default {
  components: {
    BaseToolBar,
    BaseButton
  },
  data() {
    return {
      entretiens: [],
      loading: false,

      typesEntretien: [
        { id: 0, description: "Entretien" },
        { id: 1, description: "Audit" },
        { id: 2, description: "Contrôle technique" }
      ],
      // Nombre de jours avant l'expiration pour afficher l'alerte
      joursAvantAlerte: 30
    };
  },
  mounted() {
    this.fetchData();
  },

  methods: {
    fetchData() {
      this.loading = true;

      this.$http
        .get("/vehicules-entretiens")
        .then(response => {
          if (typeof response.data === "string") {
            console.warn(
              "Les données reçues sont au format texte, pas au format JSON"
            );
            this.entretiens = [];
          } else {
            this.entretiens = response.data;
          }

          this.$nextTick(() => {
            this.initCharts();
          });
        })
        .catch(error => {
          console.error(
            "Erreur lors du chargement des données:",
            errHandler(error)
          );
          this.$toast.error("Impossible de charger les données des entretiens");
          this.entretiens = [];
        })
        .finally(() => {
          this.loading = false;
        });
    },

    getMonthlyData() {
      const monthlyData = {};
      const monthlyDataByType = {};
      const now = moment();
      const sixMonthsAgo = moment().subtract(6, "months");

      // Initialiser les types d'entretien
      this.typesEntretien.forEach(type => {
        monthlyDataByType[type.id] = {};
      });

      // Initialiser les 6 derniers mois avec des compteurs à 0
      for (
        let m = moment(sixMonthsAgo);
        m.isSameOrBefore(now, "month");
        m.add(1, "month")
      ) {
        const monthKey = m.format("YYYY-MM");
        monthlyData[monthKey] = 0;

        this.typesEntretien.forEach(type => {
          monthlyDataByType[type.id][monthKey] = 0;
        });
      }

      // Remplir les données réelles
      this.entretiens.forEach(entretien => {
        if (!entretien.date_realisation) return;

        const monthKey = moment(entretien.date_realisation).format("YYYY-MM");
        if (monthlyData[monthKey] !== undefined) {
          monthlyData[monthKey]++;

          const typeId = parseInt(entretien.type);
          if (
            monthlyDataByType[typeId] &&
            monthlyDataByType[typeId][monthKey] !== undefined
          ) {
            monthlyDataByType[typeId][monthKey]++;
          }
        }
      });

      // Formater pour ApexCharts
      const labels = Object.keys(monthlyData).map(monthKey =>
        moment(monthKey, "YYYY-MM").format("MMM YYYY")
      );

      // Préparer les données par type
      const countsByType = {};
      this.typesEntretien.forEach(type => {
        countsByType[type.id] = labels.map((_, index) => {
          const monthKey = Object.keys(monthlyData)[index];
          return monthlyDataByType[type.id][monthKey] || 0;
        });
      });

      return {
        labels,
        counts: Object.values(monthlyData),
        countsByType
      };
    },
    getTypeData() {
      const series = [];
      const labels = [];

      this.typesEntretien.forEach(type => {
        const count = this.entretiens.filter(
          e => e.type !== null && parseInt(e.type) === type.id
        ).length;
        series.push(count);
        labels.push(type.description);
      });

      return { series, labels };
    },

    // Méthode pour obtenir les données de status
    getStatusData() {
      const expired = this.entretiensExpires.length;
      const nearExpiration = this.entretiensProcheExpiration.length;
      const current = this.entretiensActuels.length;

      return {
        series: [expired, nearExpiration, current],
        labels: ["Expirés", "À prévoir", "À jour"]
      };
    },

    exportStats() {
      this.loading = true;

      this.$http
        .get("/vehicules-entretiens-export", {
          responseType: "blob"
        })
        .then(response => {
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;

          const contentDisposition = response.headers["content-disposition"];
          let filename = "entretiens-export.xls";

          if (contentDisposition) {
            const filenameMatch = contentDisposition.match(/filename="(.+)"/);
            if (filenameMatch && filenameMatch.length === 2) {
              filename = filenameMatch[1];
            }
          }

          link.setAttribute("download", filename);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          this.$toast.success("Export téléchargé avec succès");
        })
        .catch(error => {
          console.error("Erreur lors de l'export:", errHandler(error));
          this.$toast.error("Impossible d'exporter les données");
        })
        .finally(() => {
          this.loading = false;
        });
    },

    getTypeLabel(typeId) {
      const type = this.typesEntretien.find(t => t.id === parseInt(typeId));
      return type ? type.description : "Non défini";
    },

    getTypeBadgeClass(typeId) {
      typeId = parseInt(typeId);
      switch (typeId) {
        case 0:
          return "badge-primary"; // Entretien
        case 1:
          return "badge-info"; // Audit
        case 2:
          return "badge-warning"; // Contrôle technique
        default:
          return "badge-secondary";
      }
    },

    getStatusBadgeClass(entretien) {
      if (this.isEntretienExpire(entretien)) {
        return "badge-danger";
      }
      if (this.isEntretienProcheExpiration(entretien)) {
        return "badge-warning";
      }
      return "badge-success";
    },

    getStatusText(entretien) {
      if (this.isEntretienExpire(entretien)) {
        return "Expiré";
      }
      if (this.isEntretienProcheExpiration(entretien)) {
        const jours = this.getJoursAvantExpiration(entretien);
        return `À prévoir (${jours} jour${jours > 1 ? "s" : ""})`;
      }
      return "À jour";
    },

    getEntretiensByType(typeId) {
      return this.entretiens.filter(e => parseInt(e.type) === parseInt(typeId));
    },

    isEntretienExpire(entretien) {
      if (!entretien.date_fin_validite) return false;
      return moment(entretien.date_fin_validite).isBefore(moment());
    },

    isEntretienProcheExpiration(entretien) {
      if (!entretien.date_fin_validite) return false;
      if (this.isEntretienExpire(entretien)) return false;

      const joursAvantExpiration = moment(entretien.date_fin_validite).diff(
        moment(),
        "days"
      );
      return joursAvantExpiration <= this.joursAvantAlerte;
    },

    getJoursAvantExpiration(entretien) {
      if (!entretien.date_fin_validite) return null;
      return moment(entretien.date_fin_validite).diff(moment(), "days");
    },

    formatDate(date) {
      if (!date) return "Non défini";
      return moment(date).format("DD/MM/YYYY");
    }
  },
  computed: {
    monthlySeries() {
      const monthlyData = this.getMonthlyData();

      return this.typesEntretien.map(type => {
        return {
          name: type.description,
          data: monthlyData.countsByType[type.id]
        };
      });
    },

    monthlyChartOptions() {
      const monthlyData = this.getMonthlyData();
      return {
        chart: {
          type: "bar",
          height: 300,
          stacked: true,
          toolbar: {
            show: true
          },
          animations: {
            enabled: true,
            easing: "easeinout",
            speed: 800
          },
          background: "transparent"
        },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: "55%",
            endingShape: "rounded"
          }
        },
        dataLabels: {
          enabled: false
        },
        stroke: {
          show: true,
          width: 2,
          colors: ["transparent"]
        },
        colors: ["#007bff", "#17a2b8", "#ffc107"],
        xaxis: {
          categories: monthlyData.labels
        },
        yaxis: {
          title: {
            text: "Nombre d'entretiens"
          }
        },
        fill: {
          opacity: 1
        },
        tooltip: {
          y: {
            formatter: function(val) {
              return val + " entretiens";
            }
          }
        },
        legend: {
          position: "top"
        },
        theme: {
          mode:
            this.$store && this.$store.getters["colors/darkMode"]
              ? "dark"
              : "light"
        }
      };
    },

    typeSeries() {
      const typeData = this.getTypeData();
      return typeData.series;
    },

    typeChartOptions() {
      const typeData = this.getTypeData();
      return {
        chart: {
          type: "pie",
          height: 300,
          background: "transparent"
        },
        labels: typeData.labels,
        colors: ["#007bff", "#17a2b8", "#ffc107"],
        dataLabels: {
          formatter: function(val) {
            return val.toFixed(1) + "%";
          }
        },
        tooltip: {
          y: {
            formatter: function(val, { w }) {
              const total = w.globals.seriesTotals.reduce((a, b) => a + b, 0);
              const percent = ((val / total) * 100).toFixed(1);
              return `${val} (${percent}%)`;
            }
          }
        },
        legend: {
          position: "bottom"
        },
        theme: {
          mode:
            this.$store && this.$store.getters["colors/darkMode"]
              ? "dark"
              : "light"
        }
      };
    },

    statusSeries() {
      const statusData = this.getStatusData();
      return statusData.series;
    },

    statusChartOptions() {
      const statusData = this.getStatusData();
      return {
        chart: {
          type: "donut",
          height: 300,
          background: "transparent"
        },
        labels: statusData.labels,
        colors: ["#dc3545", "#ffc107", "#28a745"],
        dataLabels: {
          formatter: function(val) {
            return val.toFixed(1) + "%";
          }
        },
        tooltip: {
          y: {
            formatter: function(val, { w }) {
              const total = w.globals.seriesTotals.reduce((a, b) => a + b, 0);
              const percent = ((val / total) * 100).toFixed(1);
              return `${val} (${percent}%)`;
            }
          }
        },
        legend: {
          position: "bottom"
        },
        plotOptions: {
          pie: {
            donut: {
              size: "50%",
              labels: {
                show: true,
                total: {
                  show: true,
                  label: "Total",
                  formatter: function(w) {
                    const total = w.globals.seriesTotals.reduce(
                      (a, b) => a + b,
                      0
                    );
                    return total;
                  }
                }
              }
            }
          }
        },
        theme: {
          mode:
            this.$store && this.$store.getters["colors/darkMode"]
              ? "dark"
              : "light"
        }
      };
    },
    entretiensExpires() {
      return this.entretiens.filter(entretien =>
        this.isEntretienExpire(entretien)
      );
    },

    entretiensProcheExpiration() {
      return this.entretiens.filter(entretien =>
        this.isEntretienProcheExpiration(entretien)
      );
    },

    entretiensActuels() {
      return this.entretiens.filter(
        entretien =>
          !this.isEntretienExpire(entretien) &&
          !this.isEntretienProcheExpiration(entretien)
      );
    },

    entretiensUrgents() {
      // Combiner les entretiens expirés et ceux proches de l'expiration, puis trier par date
      return [
        ...this.entretiensExpires,
        ...this.entretiensProcheExpiration
      ].sort((a, b) => {
        // Si les deux ont une date de fin de validité, trier par date croissante
        if (a.date_fin_validite && b.date_fin_validite) {
          return moment(a.date_fin_validite).diff(moment(b.date_fin_validite));
        }
        // Sinon, mettre ceux sans date à la fin
        if (!a.date_fin_validite) return 1;
        if (!b.date_fin_validite) return -1;
        return 0;
      });
    },

    derniersEntretiens() {
      // Filtrer les entretiens qui ont une date de réalisation
      const entretiensAvecDate = this.entretiens.filter(
        e => e.date_realisation
      );

      // Trier par date décroissante (plus récent d'abord)
      return [...entretiensAvecDate]
        .sort((a, b) => {
          const dateA = new Date(a.date_realisation);
          const dateB = new Date(b.date_realisation);
          return dateB - dateA;
        })
        .slice(0, 10); // Prendre les 10 premiers résultats
    }
  }
};
</script>

<style lang="css">
.card {
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  transition: all 0.3s ease;
}

.card:hover {
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
  transform: translateY(-2px);
}

.card-header {
  font-weight: 500;
}

.table th {
  background-color: #f8f9fa;
}

.bg-danger-light {
  background-color: rgba(220, 53, 69, 0.1);
}

.bg-warning-light {
  background-color: rgba(255, 193, 7, 0.1);
}

.progress {
  border-radius: 0.5rem;
  overflow: hidden;
}

.badge {
  padding: 0.35em 0.65em;
  font-size: 0.75em;
}

.badge-danger {
  background-color: #dc3545;
  color: white;
}

.badge-warning {
  background-color: #ffc107;
  color: #212529;
}

.badge-info {
  background-color: #17a2b8;
  color: white;
}

.badge-success {
  background-color: #28a745;
  color: white;
}

.badge-primary {
  background-color: #007bff;
  color: white;
}

.border-primary {
  border-top: 4px solid #007bff !important;
}

.border-info {
  border-top: 4px solid #17a2b8 !important;
}

.border-success {
  border-top: 4px solid #28a745 !important;
}

.border-warning {
  border-top: 4px solid #ffc107 !important;
}

.border-danger {
  border-top: 4px solid #dc3545 !important;
}

.chart-container {
  padding: 10px;
  background-color: #f8f9fa;
  border-radius: 8px;
}
</style>
